import { apiClient } from "@apis/apiClient";
import type {
  AIPrompt,
  CreateAIPromptParams,
  UpdateAIPromptParams,
  AISummaryParams,
  AISummaryResponse,
  MCPToolName,
  ApiResponse,
  AiChatConversation,
  SendChatMessageParams,
  StartChatParams,
  AiChatConsumption,
} from "./aiClient.dto";

/**
 * AI API Client
 *
 * Provides methods for interacting with AI-related endpoints including
 * conversations, summaries, and prompt management.
 * Uses DTOs from aiClient.dto.ts for type safety.
 */
const aiClient = {
  /**
   * Get all AI prompts
   * @returns Array of AI prompts
   */
  getPrompts(): Promise<ApiResponse<AIPrompt[]>> {
    return apiClient.get("/rest/ai/aiprompt");
  },

  /**
   * Get a specific AI prompt by ID
   * @param id - Prompt ID
   * @returns AI prompt
   */
  getPrompt(id: string): Promise<ApiResponse<AIPrompt>> {
    return apiClient.get(`/rest/ai/aiprompt/${id}`);
  },

  /**
   * Create a new AI prompt
   * @param prompt - Prompt creation parameters
   * @returns Created AI prompt
   */
  createPrompt(prompt: CreateAIPromptParams): Promise<ApiResponse<AIPrompt>> {
    return apiClient.post("/rest/ai/aiprompt", prompt);
  },

  /**
   * Update an existing AI prompt
   * @param id - Prompt ID
   * @param prompt - Prompt update parameters
   * @returns Updated AI prompt
   */
  updatePrompt(
    id: string,
    prompt: UpdateAIPromptParams
  ): Promise<ApiResponse<AIPrompt>> {
    return apiClient.put(`/rest/ai/aiprompt/${id}`, prompt);
  },

  /**
   * Delete an AI prompt
   * @param id - Prompt ID
   * @returns Deletion response
   */
  deletePrompt(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete(`/rest/ai/aiprompt/${id}`);
  },

  /**
   * Get all available MCP tool names
   * @returns Array of MCP tool names
   */
  getMcpToolNames(): Promise<ApiResponse<MCPToolName[]>> {
    return apiClient.get("/rest/ai/mcp-tool-names");
  },

  /**
   * Get AI-generated summary
   * @param params - Summary generation parameters
   * @returns AI summary response
   */
  getSummary(params: AISummaryParams): Promise<ApiResponse<AISummaryResponse>> {
    const {
      entityId,
      key,
      refresh = false,
      extraContext = {},
      webSocketSessionId,
      requestId,
    } = params;
    let url = `/rest/ai/summary?entityId=${entityId}&key=${key}&refresh=${refresh}&extraContext=${encodeURIComponent(
      JSON.stringify(extraContext)
    )}`;

    // Add WebSocket session ID if provided for async progress updates
    if (webSocketSessionId) {
      url += `&webSocketSessionId=${encodeURIComponent(webSocketSessionId)}`;
    }

    // Add request ID if provided for correlating WebSocket messages
    if (requestId) {
      url += `&requestId=${encodeURIComponent(requestId)}`;
    }

    return apiClient.put(url);
  },

  // =====================
  // AI Chat API Methods
  // =====================

  /**
   * Start a new AI chat conversation
   * @param params - Start chat parameters
   * @returns New conversation response
   */
  startChat(
    params: StartChatParams
  ): Promise<ApiResponse<AiChatConversation>> {
    let url = "/rest/ai/chat/start";
    if (params.webSocketSessionId) {
      url += `?webSocketSessionId=${encodeURIComponent(params.webSocketSessionId)}`;
    }
    return apiClient.put(url);
  },

  /**
   * Send a message in an AI chat conversation
   * @param params - Send message parameters
   * @returns Updated conversation response
   */
  sendChatMessage(
    params: SendChatMessageParams
  ): Promise<ApiResponse<AiChatConversation>> {
    const { conversationId, message, webSocketSessionId, requestId } = params;
    let url = `/rest/ai/chat/message?message=${encodeURIComponent(message)}`;
    if (conversationId) {
      url += `&conversationId=${conversationId}`;
    }
    if (webSocketSessionId) {
      url += `&webSocketSessionId=${encodeURIComponent(webSocketSessionId)}`;
    }
    if (requestId) {
      url += `&requestId=${encodeURIComponent(requestId)}`;
    }
    return apiClient.put(url);
  },

  /**
   * Get chat conversation history for the current user
   * @param limit - Maximum number of conversations to return
   * @returns Array of chat conversations
   */
  getChatHistory(
    limit: number = 20
  ): Promise<ApiResponse<AiChatConversation[]>> {
    return apiClient.get(`/rest/ai/chat/history?limit=${limit}`);
  },

  /**
   * Get a specific chat conversation by ID
   * @param conversationId - Conversation ID
   * @returns Chat conversation details
   */
  getConversation(
    conversationId: number
  ): Promise<ApiResponse<AiChatConversation>> {
    return apiClient.get(`/rest/ai/chat/${conversationId}`);
  },

  /**
   * Delete a specific chat conversation
   * @param conversationId - Conversation ID
   * @returns Deletion response
   */
  deleteConversation(conversationId: number): Promise<ApiResponse<void>> {
    return apiClient.delete(`/rest/ai/chat/${conversationId}`);
  },

  /**
   * Clear all chat history for the current user
   * @returns Deletion response
   */
  clearChatHistory(): Promise<ApiResponse<void>> {
    return apiClient.delete("/rest/ai/chat/history");
  },

  /**
   * Cancel an ongoing AI chat request
   * @param webSocketSessionId - WebSocket session ID
   * @returns Cancellation response
   */
  cancelChat(webSocketSessionId: string): Promise<ApiResponse<void>> {
    return apiClient.put(
      `/rest/ai/chat/cancel?webSocketSessionId=${encodeURIComponent(webSocketSessionId)}`
    );
  },

  /**
   * Get AI chat consumption data (quota, usage, daily breakdown)
   * @param organisationId - Optional organisation ID (for partner/system users viewing child organisations)
   * @returns AI chat consumption data
   */
  getConsumption(organisationId?: number): Promise<ApiResponse<AiChatConsumption>> {
    let url = "/rest/ai/chat/consumption";
    if (organisationId) {
      url += `?organisationId=${organisationId}`;
    }
    return apiClient.get(url);
  },

  // =====================
  // Admin API Methods
  // =====================

  /**
   * Get any chat conversation by ID (admin only)
   * @param conversationId - Conversation ID
   * @returns Chat conversation details
   */
  getConversationAdmin(
    conversationId: number
  ): Promise<ApiResponse<AiChatConversation>> {
    return apiClient.get(`/rest/ai/admin/chat/${conversationId}`);
  },

  // =====================
  // AI Opt-in Methods
  // =====================

  /**
   * Get AI opt-in status for the current organisation
   * @returns AI opt-in status
   */
  getAiOptInStatus(): Promise<ApiResponse<{ enabled: boolean }>> {
    return apiClient.get("/rest/ai/opt-in");
  },

  /**
   * Toggle AI opt-in for the current organisation
   * @param enabled - Whether to enable or disable AI features
   * @returns Updated AI opt-in status
   */
  toggleAiOptIn(enabled: boolean): Promise<ApiResponse<{ enabled: boolean }>> {
    return apiClient.put(`/rest/ai/opt-in?enabled=${enabled}`);
  },
};

export default aiClient;
